import React from "react";
import { useParams } from "react-router-dom";
import { useFilterContext } from "context/FilterContext/Filter.context";
import { MatchingParamsType } from "router/navigationTypes";

import { countsForProductClass } from "../helpers/countsForProductClass";
import { getProductName } from "../helpers/filterProductsByName";
import { getProductBrand } from "../helpers/getProductBrand";
import { groupMatchedProductsByBrand, MatchedProductCount } from "../helpers/groupMatchedProductsByBrand";

import { FacetsValueFlatTreeType } from "modules/FacetsModule/FacetsModule.types";
import { useGetFacetsValuesWithoutContext } from "modules/FacetsModule/hooks/useGetFacetsValues";
import { useGetTaxonomyItemsInBatches } from "modules/MatchingModule/api/useGetTaxonomyItemsInBatches";
import { useGenerateMatchingFacetsApiUrl } from "modules/MatchingModule/hooks/useGenerateMatchingFacetsApiUrl";

import { BrandSource, MATCHED_ITEM_KEY } from "../consts";

/**
 * Builds the panel's brand list (product lines for food) from the rows in the current view.
 *
 * Rows only store the id of the product they were matched to (metadata.match.final), and brands
 * live in the taxonomy, a separate database. So it takes two requests:
 * 1. Matches API: GET .../facets?fields=matched_item returns every matched product id with its
 *    row count, under the table's current filters (not only the 50 rows loaded on screen).
 * 2. Taxonomy API: GET /item/?ids=... returns each of those products with its brand or product line.
 * The products are then grouped under their brand, adding up the row counts.
 */
export const useMatchedBrands = (source: BrandSource | undefined, enabled: boolean) => {
  const isEnabled = enabled && !!source;

  const { jobId = "", datasetName = "" } = useParams<MatchingParamsType>();
  const { getAllowedFilterParamsWithExcludedKeys } = useFilterContext();
  const generateApiUrl = useGenerateMatchingFacetsApiUrl(jobId, datasetName);

  // Step 1: the matched_item facet, sent with the same filters the Filters drawer's facets use.
  // One response serves all three tabs; each tab keeps its own product class below.
  const countsUrl = generateApiUrl(MATCHED_ITEM_KEY, getAllowedFilterParamsWithExcludedKeys([]));
  const { data, isLoading: isCountsQueryLoading, isError: isCountsError } = useGetFacetsValuesWithoutContext(
    MATCHED_ITEM_KEY,
    countsUrl,
    isEnabled
  );

  const counts = React.useMemo<MatchedProductCount[]>(
    () => (source ? countsForProductClass((data as FacetsValueFlatTreeType).value || [], source.productClass) : []),
    [data, source]
  );

  // Step 2: each matched product's brand, from the taxonomy (only ids not already cached are requested)
  const productIds = React.useMemo(() => counts.map(({ id }) => id), [counts]);
  const products = useGetTaxonomyItemsInBatches(productIds, isEnabled);

  const brands = React.useMemo(() => {
    if (!source) return [];
    return groupMatchedProductsByBrand(
      counts,
      products.items.map((product) => ({
        id: product.id,
        name: getProductName(product),
        brand: getProductBrand(product, source),
      }))
    );
  }, [counts, products.items, source]);

  return {
    brands,
    isLoading: (isEnabled && isCountsQueryLoading) || products.isLoading,
    isError: isCountsError || products.isError,
  };
};
